// /explore — browse: the stats dashboard, top lists and category tabs.
// Searching lives on /search; the box here suggests as you type and hands
// the query over on Enter. Old /explore?q= links (and the Pulse ?actor= /
// ?family= click-throughs) redirect there.
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import ExploreHeader from './ExploreHeader';
import TopDashboard from './TopDashboard';
import { searchEntities, fetchTopReferenced, fetchExploreCategories } from '../../lib/exploreApi';
import type {
  ExploreSearchResponse, TopReferencedResponse, TopReferencedParams, ExploreCategoriesResponse,
  ExploreSearchResult,
} from '../../lib/exploreApi';
import { entityDetailPath, searchPath as searchUrl } from '../../lib/paths';

const BROWSE_PAGE_SIZE = 15;

function quote(v: string): string {
  return /[\s()"]/.test(v) ? `"${v.replace(/"/g, '')}"` : v;
}

/** An Explore tab key as a search filter: group:network → type:network. */
function typeTerm(category: string): string {
  return `type:${category.startsWith('group:') ? category.slice('group:'.length) : category}`;
}

/** Where a legacy search-style Explore URL should go, or null to stay. */
function legacySearchTarget(p: URLSearchParams): string | null {
  const q = p.get('q') || '';
  const cat = p.get('cat') || '';
  const actor = p.get('actor') || '';
  const family = p.get('family') || '';
  let query = '';
  if (actor) query = `type:actor ${quote(actor)}`;
  else if (family) query = `type:family ${quote(family)}`;
  else if (q) query = cat ? `${typeTerm(cat)} ${q}` : q;
  if (!query) return null;
  const sort = p.get('sort');
  return searchUrl(query) + (sort && sort !== 'relevance' ? `&sort=${encodeURIComponent(sort)}` : '');
}

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlCategory = searchParams.get('cat') || '';
  const urlCountry = searchParams.get('country') || '';
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const [inputValue, setInputValue] = useState('');
  const redirectTo = useMemo(() => legacySearchTarget(searchParams), [searchParams]);

  // Bare ?country= (Pulse tile) → browse actors of that country.
  const normalized = useRef(false);
  useEffect(() => {
    if (normalized.current || redirectTo) return;
    normalized.current = true;
    if (urlCountry && !urlCategory) {
      const params = new URLSearchParams(searchParams);
      params.set('cat', 'actor');
      setSearchParams(params, { replace: true });
    }
  }, [redirectTo, urlCountry, urlCategory, searchParams, setSearchParams]);

  const goSearch = useCallback((q: string) => {
    if (q.trim()) navigate(searchUrl(q));
  }, [navigate]);

  const pickEntity = useCallback((entity: ExploreSearchResult) => {
    navigate(entityDetailPath(entity));
  }, [navigate]);

  const setCategory = useCallback((cat: string) => {
    const params = new URLSearchParams(searchParams);
    if (cat) params.set('cat', cat);
    else params.delete('cat');
    params.delete('page');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const setPage = useCallback((page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) params.set('page', String(page));
    else params.delete('page');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Category groups — shared queryKey with TopDashboard/ExploreHeader, so
  // this is served from the same cache entry.
  const { data: catsData } = useQuery<ExploreCategoriesResponse>({
    queryKey: ['explore-categories'],
    queryFn: fetchExploreCategories,
    staleTime: 60_000,
  });

  // Only fetch the top lists the dashboard actually renders: none on the
  // landing view (stats banner only), one for a single category, the
  // group's members for a group:<g> selection. null = keys not known yet.
  const topParams = useMemo<TopReferencedParams | null>(() => {
    if (!urlCategory) return { lists: false };
    if (urlCategory.startsWith('group:')) {
      if (!catsData) return null;
      const grp = catsData.groups.find(g => g.key === urlCategory.slice('group:'.length));
      return { categories: grp?.categories.map(c => c.key) ?? [] };
    }
    return { categories: [urlCategory] };
  }, [urlCategory, catsData]);

  const { data: topData, isLoading: topLoading } = useQuery<TopReferencedResponse>({
    queryKey: ['explore-top-referenced', topParams],
    queryFn: () => fetchTopReferenced(topParams ?? undefined),
    enabled: !redirectTo && topParams !== null,
    staleTime: 60_000,
    // Keep the stats banner on screen while a new category's lists load.
    placeholderData: keepPreviousData,
  });

  // Paginated browse of the selected category tab.
  const { data: browseData, isLoading: browseLoading } = useQuery<ExploreSearchResponse>({
    queryKey: ['explore-browse', urlCategory, urlPage, urlCountry],
    queryFn: () => searchEntities({
      query: '',
      category: urlCategory || undefined,
      country: urlCountry || undefined,
      sort_by: 'doc_count',
      limit: BROWSE_PAGE_SIZE,
      offset: (urlPage - 1) * BROWSE_PAGE_SIZE,
    }),
    enabled: !redirectTo && !!urlCategory,
    staleTime: 60_000,
  });

  // Right-click "Search" on a top-list item: search it within its type.
  const handleDashboardSearch = useCallback((value: string, category: string) => {
    navigate(searchUrl(category ? `${typeTerm(category)} ${quote(value)}` : quote(value)));
  }, [navigate]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return (
    <div className="explore-page">
      <ExploreHeader
        query={inputValue}
        onQueryChange={setInputValue}
        onSubmit={goSearch}
        onPickEntity={pickEntity}
        category={urlCategory}
        onCategoryChange={setCategory}
      />

      <TopDashboard
        data={topData}
        isLoading={topLoading}
        onItemClick={handleDashboardSearch}
        onCategorySelect={setCategory}
        category={urlCategory}
        browseItems={browseData?.results}
        browseTotal={browseData?.total ?? 0}
        browseLoading={browseLoading}
        page={urlPage}
        pageSize={BROWSE_PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
